import { useTranslation } from 'react-i18next'

import Loading from '../../../../../loading/Loading'
import { PassIntervals, usePassFormat } from '../passManagerParts'
import { ScheduleCourts, ScheduleDays, Trend, changedClass } from '../../../scheduleCard/scheduleParts'

import { PassProduct } from '../../../../../../types/booking/booking'

type PassManagerVersionsProps = {
  /** Every version of the pass, newest first; null while loading. */
  history: PassProduct[] | null
  courtNames: Map<number | undefined, string>
}

/**
 * Previous versions of one pass. Each entry shows its own terms, with what it changed from the version before it
 * highlighted; changed amounts carry an up or down arrow.
 */
export const PassManagerVersions = ({ history, courtNames }: PassManagerVersionsProps) => {
  const { t } = useTranslation('passes')
  const format = usePassFormat()

  if (history === null) {
    return (
      <div className="schedule-card-versions">
        <Loading variant="inline" />
      </div>
    )
  }

  // Newest first: the first entry is the version the card already shows.
  const previous = history.slice(1)

  return (
    <div className="schedule-card-versions">
      <div className="schedule-card-versions-title tiny muted">{t('passes.manager.card.previous-versions')}</div>
      {previous.length === 0 && <div className="tiny muted">{t('passes.manager.card.no-previous-versions')}</div>}
      {previous.map((version, index) => {
        // The version this one replaced (the next one down the list); the oldest has nothing to compare with.
        const replaced = previous[index + 1]
        const changed = <K extends keyof PassProduct>(key: K) => replaced !== undefined && version[key] !== replaced[key]

        return (
          <div key={version.versionId} className="schedule-card-version">
            <div className="schedule-card-version-header">
              <div className="schedule-card-version-title">
                <span className="mono fw-semibold">{t('passes.manager.card.version', { version: version.version })}</span>
                <span className="tiny muted">– {format.since(version)}</span>
                {changed('name') && <span className="has-changed schedule-card-version-mark">{version.name}</span>}
              </div>
            </div>

            {/* Same columns as the card (terms, when, where), with courts at the card's fixed width. */}
            <div className="schedule-card-version-facts">
              <div className="schedule-card-version-fact">
                <span className={`mono fw-semibold schedule-card-version-mark${changedClass(changed('price'))}`}>
                  {format.price(version)}
                  <Trend value={version.price} before={replaced?.price} />
                </span>
                <span className={`mono schedule-card-version-mark${changedClass(changed('includedMinutes'))}`}>
                  {format.minutes(version)}
                  <Trend value={version.includedMinutes} before={replaced?.includedMinutes} />
                </span>
                <span className={`mono schedule-card-version-mark${changedClass(changed('validityDays'))}`}>
                  {format.validity(version)}
                  <Trend value={version.validityDays} before={replaced?.validityDays} />
                </span>
              </div>
              <div className="schedule-card-version-fact">
                <PassIntervals intervals={version.intervals} previous={replaced?.intervals} />
                <ScheduleDays weekdays={version.weekdays} previous={replaced?.weekdays} />
              </div>
              <div className="schedule-card-version-fact is-courts">
                <ScheduleCourts courtIds={version.courtIds} courtNames={courtNames} previous={replaced?.courtIds} />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
